import React, { Fragment, memo, useEffect, useRef } from 'react';
import { connect } from 'react-redux';
import { get, isEmpty, uniq } from 'lodash-es';
import classnames from 'classnames';
import { useOnScreen } from 'site-modules/shared/hooks/use-on-screen';
import { bindToPath, connectToModel } from 'client/data/luckdragon/redux/react-binding';
import { PageModel } from 'client/data/models/page';
import { VisitorModel } from 'client/data/models/visitor';
import { NativeAdsCreativeConfigModel } from 'client/data/models/native-ads-creative-config';
import { isAdEnabledByDma } from 'client/site-modules/shared/constants/ads';
import {
  getAdParamsByVehicle,
  getCreativeConfigAdPathByVehicle,
  isEmptyCreativeConfig,
  transformCreativeConfig,
} from 'client/site-modules/shared/components/native-ad/utils/utils';
import { fireNativeAdLoadTracking } from 'client/engagement-handlers/site-served-ads-engagement-handler/site-served-ads-engagement-handler';
import { getIvtClassName } from 'client/utils/get-ias-data';
import { REQUIRED_PARAMS } from 'site-modules/shared/components/native-ad/utils/constants';

/**
 * HOC for making decision what kind of ad should be displayed based on various data
 * @param {function} WrappedComponent
 * @returns {SiteServedAdWrapper}
 */
export function siteServedAdWrapper(WrappedComponent) {
  function SiteServedAdWrapper(props) {
    const {
      adType,
      position,
      creativeConfig = null,
      vehicle,
      currentVehicle,
      siteServedAdsEnabled,
      visitorLocation,
      showOnlyDFP,
      showOnlySiteServed = false,
      iasData,
      isEnableIAS,
      wrapperTag: WrapperTag = 'div',
      wrapperClass,
      placeholderName,
      placeholderComponent: PlaceholderComponent = 'div',
      placeholderComponentProps,
    } = props;
    const { breakpoints, currentBreakpoint, pageLocation, ...otherProps } = props;
    const siteServedAdsUpdater = creativeConfig?.siteServedAdsUpdater ?? 0;

    const adRef = useRef();
    const [isIntersecting, disconnect] = useOnScreen(adRef, { rootMargin: '0px' }, true, siteServedAdsUpdater);

    const emptyCreativeConfig = isEmptyCreativeConfig(creativeConfig) || get(creativeConfig, 'excluded', false);
    const excludedFromSiteServed = emptyCreativeConfig || !isAdEnabledByDma(visitorLocation);

    const canUseDFPFallback = excludedFromSiteServed || !siteServedAdsEnabled;
    const useDFPFallback = showOnlySiteServed ? false : canUseDFPFallback;

    useEffect(() => {
      const adNamesContainer = document.querySelector('[data-ad-names]');
      if (adNamesContainer) {
        const adLoaded = get(adNamesContainer, 'dataset.adRendered', '').split(',');
        adNamesContainer.dataset.adRendered = uniq([...adLoaded, adType.AD_NAME]).join();
        const adStyleNames = get(adNamesContainer, 'dataset.adStyleNames', '').split(',');
        adNamesContainer.dataset.adStyleNames = uniq([...adStyleNames, adType.NATIVE_STYLE]).join();
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
      if (emptyCreativeConfig && isIntersecting) {
        fireNativeAdLoadTracking('unfilled', adType.NATIVE_STYLE, siteServedAdsUpdater, position, '-1');
        disconnect();
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [emptyCreativeConfig, siteServedAdsUpdater, isIntersecting]);

    const ivtClassName = getIvtClassName(iasData, isEnableIAS);

    const placeholderDef = adType?.PLACEHOLDER?.[placeholderName];
    const placeholderEmptyContainer = placeholderDef ? (
      <div style={placeholderDef.style} className={classnames(wrapperClass, ivtClassName)} />
    ) : null;

    // ad unit will be displayed for specific breakpoints, waiting for resolve of the current breakpoint
    if (!isEmpty(breakpoints) && !currentBreakpoint) {
      return placeholderEmptyContainer;
    }
    if (!isEmpty(breakpoints) && currentBreakpoint && !breakpoints[currentBreakpoint]) {
      return placeholderEmptyContainer;
    }
    // pending configuration
    // we should resolve creative config before rendering ads. This helps to avoid unnecessary re-renders.
    if (!showOnlyDFP && siteServedAdsEnabled && creativeConfig === null) {
      return placeholderEmptyContainer;
    }

    const PlaceholderTag = placeholderDef ? 'div' : Fragment;
    const placeholderTagProps = placeholderDef
      ? {
          style: placeholderDef.style,
          className: classnames('d-flex align-items-center', wrapperClass, ivtClassName),
        }
      : {};
    const isRenderPlaceholder = showOnlySiteServed && emptyCreativeConfig && !!placeholderDef;

    /*
      vehicle - vehicle is from page context, it is general case
      currentVehicle -  vehicle is from component that is passed directly to ad unit
      */
    return (
      <PlaceholderTag {...placeholderTagProps}>
        {emptyCreativeConfig && (
          <WrapperTag
            ref={adRef}
            className="site-served-ad-empty visually-hidden"
            data-value={adType.AD_NAME}
            style={{ position: 'relative !important' }}
          />
        )}
        {isRenderPlaceholder ? (
          <PlaceholderComponent {...placeholderComponentProps} />
        ) : (
          <WrappedComponent
            {...otherProps}
            pageLocation={pageLocation}
            vehicle={currentVehicle || vehicle}
            withDFPFallback={useDFPFallback}
            withSiteServed={!canUseDFPFallback}
            ivtClassName={ivtClassName}
          />
        )}
      </PlaceholderTag>
    );
  }
  SiteServedAdWrapper.displayName = `WithSiteServedAdWrapper(${WrappedComponent.name})`;
  return SiteServedAdWrapper;
}

export const getSiteServedAdPath = params => {
  const {
    mobile,
    adType,
    vehicle,
    currentVehicle,
    creativeConfigPath,
    showOnlyDFP,
    siteServedAdsEnabled,
    pageLocation,
    adsSpaMode,
  } = params;

  /*
  vehicle - vehicle is from page context, it is general case
  currentVehicle -  vehicle is from component that is passed directly to ad unit
  */

  const targetVehicle = currentVehicle || vehicle;
  if (!siteServedAdsEnabled || showOnlyDFP) return null;

  const vehicleParams = getAdParamsByVehicle(targetVehicle);
  // vehicle params should be defined for given ad type
  const isDefined = REQUIRED_PARAMS[adType.AD_NAME].some(param => vehicleParams[param] !== 'none');
  if (!isDefined) {
    return null;
  }

  const adPath = `${
    creativeConfigPath || getCreativeConfigAdPathByVehicle(mobile, targetVehicle, pageLocation, adsSpaMode)
  }["${adType.AD_NAME}"]`;
  return adPath;
};

const stateToPropsConfig = {
  creativeConfig: bindToPath(
    ({
      mobile,
      adType,
      vehicle,
      currentVehicle,
      creativeConfigPath,
      showOnlyDFP,
      siteServedAdsEnabled,
      pageLocation,
      adsSpaMode,
    }) =>
      getSiteServedAdPath({
        mobile,
        adType,
        vehicle,
        currentVehicle,
        creativeConfigPath,
        showOnlyDFP,
        siteServedAdsEnabled,
        pageLocation,
        adsSpaMode,
      }),
    NativeAdsCreativeConfigModel,
    transformCreativeConfig
  ),
  ads: bindToPath('ads', PageModel),
  visitorLocation: bindToPath('location', VisitorModel),
};

const mapStateToProps = state => ({
  vehicle: get(state, 'pageContext.vehicle'),
  siteServedAdsEnabled: state.featureFlags.siteServedAds,
  currentBreakpoint: get(state, 'pageContext.breakpointDeprecated2'),
  iasData: state.visitor?.iasData,
  vehicleContextChange: get(state, 'pageContext.page.options.vehicleContextChange', false),
  pageLocation: get(state, 'pageContext.location'),
  adsSpaMode: get(state, 'pageContext.page.options.adsSpaMode', false),
  isEnableIAS: state.featureFlags.enableIAS,
});

export const creativeConfigPropsAreEqual = (propsPrev, propsNext) => {
  const { currentVehicle, vehicle, creativeConfig } = propsNext;
  const targetVehicle = currentVehicle || vehicle;
  return !targetVehicle || !creativeConfig?.siteServedAdsUpdater;
};
export const propsAreEqual = ({ vehicleContextChange }, { vehicle, currentVehicle }) => {
  if (vehicleContextChange) {
    return isEmpty(vehicle) || isEmpty(currentVehicle);
  }
  return isEmpty(vehicle) && isEmpty(currentVehicle);
};

export const withSiteServedAdWrapper = WrappedComponent =>
  connect(mapStateToProps)(
    memo(
      connectToModel(memo(siteServedAdWrapper(WrappedComponent), creativeConfigPropsAreEqual), stateToPropsConfig),
      propsAreEqual
    )
  );
